/**
 * "Eğitim ve SOP Takibi" bölümünün gövdesi.
 *
 * Veriyi hesaplamaz — `useTeamTraining` çıktısını gösterir. Bölüm başlığı ve
 * aç/kapa düğmesi diğer bölümlerle aynı görünsün diye üst sayfada kalır.
 */

import React, { useState } from 'react';
import {
  FaBookOpen,
  FaGraduationCap,
  FaExclamationTriangle,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaSpinner,
  FaSync,
  FaDownload,
  FaUserClock,
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { parseDueDate } from '../../utils/assignmentDeadlines';
import styles from './TeamTrainingPanel.module.css';

const formatDueDate = (value) => {
  const date = parseDueDate(value);
  return date ? date.toLocaleDateString('tr-TR') : '—';
};

const TeamTrainingPanel = ({ data, smoName }) => {
  const { rows, summary, loading, error, reload } = data;
  const [expandedId, setExpandedId] = useState(null);

  const downloadExcel = () => {
    const sheet = XLSX.utils.json_to_sheet(
      rows.flatMap((row) =>
        row.items.map((item) => ({
          'Çalışan': row.name,
          'Rol': row.role,
          'Tür': item.type,
          'Atama': item.name,
          'Son Tarih': formatDueDate(item.dueDate),
          'Durum': item.deadlineLabel,
        }))
      )
    );
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Eğitim ve SOP');
    XLSX.writeFile(book, `egitim-sop-takibi_${(smoName || 'smo').replace(/\s+/g, '-')}.xlsx`);
  };

  if (loading && rows.length === 0) {
    return (
      <div className={styles.stateBox}>
        <FaSpinner className={styles.spinner} />
        Eğitim ve SOP durumu yükleniyor...
      </div>
    );
  }

  if (error) {
    return <div className={styles.errorBanner}>{error}</div>;
  }

  if (rows.length === 0) {
    return (
      <div className={styles.stateBox}>
        <FaCheckCircle className={styles.stateIconOk} />
        Ekipteki herkes kendisine atanan SOP ve eğitimleri tamamlamış.
      </div>
    );
  }

  return (
    <>
      <div className={styles.tiles}>
        <div className={styles.tile}>
          <FaUserClock className={styles.tileIcon} />
          <div className={styles.tileBody}>
            <span className={styles.tileValue}>
              {summary.pendingPeople}
              <small> / {summary.teamSize}</small>
            </span>
            <span className={styles.tileLabel}>Tamamlamayan Çalışan</span>
          </div>
        </div>
        <div className={styles.tile}>
          <FaBookOpen className={styles.tileIcon} />
          <div className={styles.tileBody}>
            <span className={styles.tileValue}>{summary.pendingSops}</span>
            <span className={styles.tileLabel}>Bekleyen SOP</span>
          </div>
        </div>
        <div className={styles.tile}>
          <FaGraduationCap className={styles.tileIcon} />
          <div className={styles.tileBody}>
            <span className={styles.tileValue}>{summary.pendingEducations}</span>
            <span className={styles.tileLabel}>Bekleyen Eğitim</span>
          </div>
        </div>
        <div className={`${styles.tile} ${summary.overdue > 0 ? styles.tileAlert : ''}`}>
          <FaExclamationTriangle
            className={`${styles.tileIcon} ${summary.overdue > 0 ? styles.tileIconAlert : ''}`}
          />
          <div className={styles.tileBody}>
            <span className={styles.tileValue}>{summary.overdue}</span>
            <span className={styles.tileLabel}>Son Tarihi Geçmiş</span>
          </div>
        </div>
      </div>

      <div className={styles.toolbar}>
        <span className={styles.toolbarNote}>
          Atamalar role göre yapılır; çalışanın profilinde tamamladığı kayıtlar burada düşer.
        </span>
        <button className={styles.toolbarButton} onClick={reload} disabled={loading}>
          <FaSync className={loading ? styles.spinner : undefined} /> Yenile
        </button>
        <button className={styles.toolbarButton} onClick={downloadExcel}>
          <FaDownload /> Excel
        </button>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Çalışan</th>
              <th>Rol</th>
              <th>Bekleyen SOP</th>
              <th>Bekleyen Eğitim</th>
              <th>En Yakın Son Tarih</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const expanded = expandedId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={styles.row}
                    onClick={() => setExpandedId(expanded ? null : row.id)}
                  >
                    <td>
                      <span className={styles.nameCell}>
                        <span className={styles.name}>{row.name}</span>
                        {row.overdueCount > 0 && (
                          <span className={styles.overdueBadge}>
                            <FaExclamationTriangle /> {row.overdueCount} gecikmiş
                          </span>
                        )}
                      </span>
                    </td>
                    <td className={styles.muted}>{row.role}</td>
                    <td className={styles.numeric}>{row.sopCount || '—'}</td>
                    <td className={styles.numeric}>{row.educationCount || '—'}</td>
                    <td className={row.nextDeadline?.overdue ? styles.overdueText : undefined}>
                      {row.nextDeadline ? row.nextDeadline.deadlineLabel : 'Son tarih yok'}
                    </td>
                    <td className={styles.chevron}>
                      {expanded ? <FaChevronUp /> : <FaChevronDown />}
                    </td>
                  </tr>

                  {expanded && (
                    <tr className={styles.detailRow}>
                      <td colSpan={6}>
                        <div className={styles.itemList}>
                          {row.items.map((item) => (
                            <div key={`${item.type}-${item.id}`} className={styles.item}>
                              <span
                                className={`${styles.typeBadge} ${
                                  item.type === 'SOP' ? styles.typeSop : styles.typeEducation
                                }`}
                              >
                                {item.type === 'SOP' ? <FaBookOpen /> : <FaGraduationCap />}
                                {item.type}
                              </span>
                              <span className={styles.itemName} title={item.name}>
                                {item.name}
                              </span>
                              <span
                                className={`${styles.itemDeadline} ${
                                  item.overdue ? styles.overdueText : ''
                                }`}
                              >
                                {item.deadlineLabel}
                                {item.dueDate && (
                                  <small> · {formatDueDate(item.dueDate)}</small>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default TeamTrainingPanel;
